import React, { useState, useEffect, useRef, useMemo } from 'react';

interface VirtualListProps<T> {
  items: T[];
  height: number | string;
  itemHeight: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  overscan?: number;
}

function VirtualList<T>({ 
  items, 
  height, 
  itemHeight, 
  renderItem, 
  className = "",
  overscan = 5
}: VirtualListProps<T>) {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalHeight = items.length * itemHeight;
  
  // Calculate range
  const { startIndex, endIndex } = useMemo(() => {
    let containerHeight = 0;
    if (typeof height === 'number') {
        containerHeight = height;
    } else {
        // Approximate or rely on ref if purely pixel based logic needed, 
        // but for simple cases we assume the viewport is roughly the scroll window
        // If height is 100%, we might need the actual pixel height from ResizeObserver
        // For simplicity, we use the containerRef's clientHeight if available, else 0
        containerHeight = containerRef.current?.clientHeight || 500;
    }

    const start = Math.floor(scrollTop / itemHeight);
    const end = Math.ceil((scrollTop + containerHeight) / itemHeight);

    return {
      startIndex: Math.max(0, start - overscan),
      endIndex: Math.min(items.length, end + overscan)
    };
  }, [scrollTop, items.length, itemHeight, height, overscan]);

  const visibleItems = items.slice(startIndex, endIndex);
  const offsetY = startIndex * itemHeight;

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
        if (containerRef.current) {
             // Force re-render logic if needed, but onScroll usually handles dynamic bounds
        }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`overflow-y-auto relative ${className}`}
      style={{ height }}
      onScroll={onScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div 
            style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                transform: `translateY(${offsetY}px)` 
            }}
        >
          {visibleItems.map((item, index) => renderItem(item, startIndex + index))}
        </div>
      </div>
    </div>
  );
}

export default VirtualList;